import { NO_ELEMENT } from 'utils';
import { ConfiguratorBase } from 'models/ConfiguratorBase';
import { doorsKey, selectedFillingsKey, profileKey, imageBasePath } from './selector';
import styles from './elfaImages.scss';
import { profileMap } from './themes/BGH/profileAddons';
import { fillingMap } from './fillingMap';
import { ElfaDoorSpec } from 'models/ElfaDoorSpec';
import { useRef, useEffect, Reducer, useReducer, useCallback } from 'react';
import { SplashWidget, IWidget, WidgetType } from 'models/ListViewProduct';
import Splash from 'components/Shared/Splash';
import { useSelector } from 'react-redux';
import { getChannel } from 'views/ProductPage/selector';

interface OwnProps {
  widget?: IWidget | undefined;
  configurator: ConfiguratorBase;
  cdnUrl: string;
}

interface OwnState {
  scale: number;
}

interface OwnAction {
  type: 'setScale';
  scale: number;
}

const ownReducer: Reducer<OwnState, OwnAction> = (state, action) => {
  switch (action.type) {
    case 'setScale':
      return {
        ...state,
        scale: action.scale
      };
    default:
      return state;
  }
};

const initialState: OwnState = {
  scale: 1
};

const isSplashWidget = (widget: IWidget): widget is SplashWidget =>
  widget.type === WidgetType.Splash;

export default function ElfaImages({ configurator, cdnUrl, widget }: OwnProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [state, dispatch] = useReducer(ownReducer, initialState);
  const channel = useSelector(getChannel);
  const shouldRenderSplash = channel === 'GOP';

  const updateScale = useCallback(() => {
    const originalWidth = 634;
    const width = ref.current ? ref.current.offsetWidth : 0;

    dispatch({ type: 'setScale', scale: width / originalWidth });
  }, []);

  useEffect(() => {
    updateScale();
    window.addEventListener('resize', updateScale);
    return () => {
      window.removeEventListener('resize', updateScale);
    };
  }, [updateScale]);

  if (!configurator || !configurator.values) {
    return NO_ELEMENT;
  }

  const numDoors = configurator.values[doorsKey];
  const profile = configurator.values[profileKey];
  const doorBasePosition = 105 * state.scale;
  const doorWidth = 115 * state.scale;
  const imageBaseUrl = `${cdnUrl}${imageBasePath}`;
  const backgroundImgUrl = `${imageBaseUrl}doors_0${numDoors}.png`;
  const profileImageUrl = `${imageBaseUrl}profile_0${numDoors}_${profileMap[profile]}.png`;
  const selectedFillingsString = configurator.values && configurator.values[selectedFillingsKey];
  let selectedFillings: ElfaDoorSpec[] = selectedFillingsString
    ? (JSON.parse(selectedFillingsString) as ElfaDoorSpec[])
    : [];

  if (selectedFillings.length > 0) {
    selectedFillings = selectedFillings.sort((a, b) => (a.doorId > b.doorId ? 1 : -1));
  }

  const translateImage = (imageName: string, doorId: number) => {
    // Mirror images has special images to each door.
    let baseName = '';

    switch (imageName) {
      case '30001-Clear-mirror_BH':
        baseName = 'Elfa_Spegel_Klar_';
        break;
      case '30003-Grey-mirror_BH':
        baseName = 'Elfa_Spegel_Grey_';
        break;
      case '30002-Bronze-mirror_BH':
        baseName = 'Elfa_Spegel_Brons_';
        break;
    }

    if (baseName !== '') {
      return baseName + (doorId + 1).toString();
    }

    return imageName;
  };

  return (
    <div ref={ref} className={styles.imageContainer}>
      {shouldRenderSplash && widget && isSplashWidget(widget) && <Splash {...widget} />}
      {selectedFillings.map(f => (
        <img
          key={f.doorId}
          src={`${imageBaseUrl}${translateImage(
            fillingMap[f.type.toLowerCase()][f.name.toLowerCase()].image,
            f.doorId
          )}.png`}
          className={styles.fillingImage}
          style={{
            left: `${doorBasePosition + doorWidth * f.doorId}px`,
            top: `${40 * state.scale}px`,
            transform: `scale(${state.scale})`,
            transformOrigin: 'top left'
          }}
        />
      ))}
      <img src={backgroundImgUrl} className={styles.base} />
      <img
        src={profileImageUrl}
        className={styles.profile}
        style={{
          transform: `scale(${state.scale})`,
          transformOrigin: 'top left',
          left: `${100 * state.scale}px`,
          top: `${29 * state.scale}px`
        }}
      />
    </div>
  );
}
